import React, { useState, useEffect } from 'react';
import { FiX, FiFileText, FiEye, FiClock, FiUser, FiShoppingBag, FiCalendar, FiFilter, FiPlus } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { socket } from '../../socket/socket';
import AddOrderModal from '../../components/AddOrderModal';
import TableBillUpdateModal from '../../components/TableBillUpdateModal';

const OpenTables = () => {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState('open');
  const [openTables, setOpenTables] = useState([]);
  const [closedTables, setClosedTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [discount, setDiscount] = useState({ amount: 0, type: 'percentage' });
  const [payment, setPayment] = useState({ cashAmount: '', onlineAmount: '' });
  const [openCurrentPage, setOpenCurrentPage] = useState(1);
  const [closedCurrentPage, setClosedCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [gstNumber, setGstNumber] = useState('');
  const [isMc, setIsMc] = useState(false);
  const [cashOnly, setCashOnly] = useState(false);
  const [cashReceived, setCashReceived] = useState('');
  const [complimentary, setComplimentary] = useState('');
  const [totalOpenTables, setTotalOpenTables] = useState(0);
  const [totalClosedTables, setTotalClosedTables] = useState(0);
  // Add Order Modal state
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [customerData, setCustomerData] = useState({});
  // Filter states for closed bills
  const [closedBillsFilter, setClosedBillsFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  // Table bill update modal states
  const [showTableBillUpdateModal, setShowTableBillUpdateModal] = useState(false);
  const [selectedTableForUpdate, setSelectedTableForUpdate] = useState(null);


  useEffect(() => {
    fetchOpenTables();
  }, [activeTab, openCurrentPage, closedCurrentPage, itemsPerPage, closedBillsFilter, customStartDate, customEndDate]);

  useEffect(() => {
    fetchCloseTables();
  }, []);

  useEffect(() => {
    if (!user) return;

    socket.on('notification:new', (payload) => {
      fetchOpenTables()
    });
    socket.on('order:updated', (payload) => {
      fetchOpenTables()
    });

    return () => {
      socket.off('notification:new');
      socket.off('order:updated');
    };
  }, [user, showCreateOrderModal]);

  const fetchOpenTables = async () => {
    try {
      const page = activeTab === 'open' ? openCurrentPage : closedCurrentPage;
      let url = `/vendor/open-tables?status=${activeTab}&page=${page}&limit=${itemsPerPage}`;

      // Add filter parameters
      if (closedBillsFilter !== 'all') {
        const today = new Date();
        let startDate = '';
        let endDate = '';

        switch (closedBillsFilter) {
          case 'today':
            startDate = endDate = today.toISOString().split('T')[0];
            break;
          case 'yesterday':
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            startDate = endDate = yesterday.toISOString().split('T')[0];
            break;
          case 'thisWeek':
            const startOfWeek = new Date(today);
            startOfWeek.setDate(today.getDate() - today.getDay());
            startDate = startOfWeek.toISOString().split('T')[0];
            endDate = today.toISOString().split('T')[0];
            break;
          case 'thisMonth':
            const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
            startDate = startOfMonth.toISOString().split('T')[0];
            endDate = today.toISOString().split('T')[0];
            break;
          case 'custom':
            if (customStartDate && customEndDate) {
              startDate = customStartDate;
              endDate = customEndDate;
            }
            break;
        }

        if (startDate) url += `&startDate=${startDate}`;
        if (endDate) url += `&endDate=${endDate}`;
      }

      const response = await api.get(url);

      if (activeTab === 'open') {
        setOpenTables(response.data.data || []);
        setTotalOpenTables(response.data.total || 0);
      } else {
        setClosedTables(response.data.data || []);
        setTotalClosedTables(response.data.total || 0);
      }

      if (selectedTable?._id) {
        let data = response.data.data.find((data) => data._id === selectedTable?._id);
        setSelectedTable(data)
      }
    } catch (error) {
      console.error('Failed to fetch tables');
    } finally {
      setLoading(false);
    }
  };

  const fetchCloseTables = async () => {
    try {
      const page = activeTab === 'open' ? openCurrentPage : closedCurrentPage;
      let url = `/vendor/open-tables?status=closed&page=${page}&limit=${itemsPerPage}`;

      // Add filter parameters
      if (closedBillsFilter !== 'all') {
        const today = new Date();
        let startDate = '';
        let endDate = '';

        switch (closedBillsFilter) {
          case 'today':
            startDate = endDate = today.toISOString().split('T')[0];
            break;
          case 'yesterday':
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            startDate = endDate = yesterday.toISOString().split('T')[0];
            break;
          case 'thisWeek':
            const startOfWeek = new Date(today);
            startOfWeek.setDate(today.getDate() - today.getDay());
            startDate = startOfWeek.toISOString().split('T')[0];
            endDate = today.toISOString().split('T')[0];
            break;
          case 'thisMonth':
            const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
            startDate = startOfMonth.toISOString().split('T')[0];
            endDate = today.toISOString().split('T')[0];
            break;
          case 'custom':
            if (customStartDate && customEndDate) {
              startDate = customStartDate;
              endDate = customEndDate;
            }
            break;
        }

        if (startDate) url += `&startDate=${startDate}`;
        if (endDate) url += `&endDate=${endDate}`;
      }

      const response = await api.get(url);

      setClosedTables(response.data.data || []);
      setTotalClosedTables(response.data.total || 0);

      if (selectedTable?._id) {
        let data = response.data.data.find((data) => data._id === selectedTable?._id);
        setSelectedTable(data)
      }
    } catch (error) {
      console.error('Failed to fetch tables');
    } finally {
      setLoading(false);
    }
  };

  const openCloseModal = (table) => {
    setSelectedTable(table);
    setDiscount({ amount: 0, type: 'percentage' });
    setPayment({ cashAmount: '', onlineAmount: table.totalAmount });
    setIsMc(false);
    setCashOnly(false);
    setCashReceived('');
    setComplimentary('');
    setGstNumber('');
    setShowCloseModal(true);
  };

  const openBillUpdateModal = (table) => {
    // Check if table has orders with generated bills
    const billGeneratedOrders = table.orders?.filter(orderRef => 
      orderRef.orderId && orderRef.orderId.billGenerated
    ) || [];
    
    if (billGeneratedOrders.length === 0) {
      toast.error('No generated bills found for this table');
      return;
    }
    
    // Table-wise update - pass entire table data
    setSelectedTableForUpdate(table);
    setShowTableBillUpdateModal(true);
  };

  const openOrdersModal = (table) => {
    setSelectedTable(table);
    setShowOrdersModal(true);
  };

  const getDuration = (startTime) => {
    const minutes = Math.round((new Date() - new Date(startTime)) / 60000);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const hasPendingOrders = (table) => {
    return table.orders?.some(orderRef => {
      const order = orderRef.orderId;
      return order && order.status === 'pending';
    });
  };

  const isTableBillGenerated = (table) => {
    return table.orders?.every(orderRef => {
      const order = orderRef.orderId;
      return order && order.billGenerated;
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-blue-100 text-blue-800';
      case 'preparing': return 'bg-indigo-100 text-indigo-800';
      case 'ready': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getNextStatus = (currentStatus) => {
    const statusFlow = ['pending', 'accepted', 'preparing', 'ready', 'completed'];
    const currentIndex = statusFlow.indexOf(currentStatus);
    if (currentIndex === -1 || currentStatus === 'cancelled') return currentStatus;
    return statusFlow[currentIndex + 1] || 'completed';
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/vendor/orders/${orderId}/status`, { status: newStatus });
      toast.success('Order status updated');
      fetchOpenTables();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleStatusClick = (orderId, currentStatus) => {
    if (currentStatus === 'completed' || currentStatus === 'cancelled') {
      return;
    }
    const nextStatus = getNextStatus(currentStatus);
    updateOrderStatus(orderId, nextStatus);
  };

  // Add Order Functions
  const handleOpenAddOrder = (table) => {
    setShowCreateOrderModal(true);
    setCustomerData({
      customerName: table?.customerId?.fullName,
      customerMobile: table?.customerId.mobileNumber,
      tableNumber: table.tableId.tableNumber
    })
  };

  const generateBill = async () => {
    try {
      let response;

      if (activeTab === 'closed' || isTableBillGenerated(selectedTable)) {
        response = await api.get(`/vendor/tables/${selectedTable._id}/print-bill`);
      } else {
        response = await api.post(`/vendor/tables/${selectedTable.tableId._id}/close`, {
          discount: discount.amount,
          discountType: discount.type,
          cashAmount: payment.cashAmount,
          onlineAmount: payment.onlineAmount,
          gstNumber: gstNumber,
          isMc: isMc,
          complimentary: complimentary
        });
      }

      const billData = response.data.data;

      // Open print window
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Bill - ${billData.billNumber}</title>
            <style>
              @media print {
                @page { margin: 0; size: 80mm auto; }
                body { margin: 0; }
              }
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { 
                font-family: 'Courier New', monospace; 
                width: 80mm;
                padding: 5mm;
                font-size: 11px;
                line-height: 1.4;
              }
              .header {
                text-align: center;
                margin-bottom: 10px;
              }
              .header img.logo {
                width: 60px;
                height: 60px;
                object-fit: contain;
                margin-bottom: 8px;
                border-radius: 8px;
              }
              .header h1 {
                font-size: 20px;
                font-weight: bold;
                margin-bottom: 4px;
                letter-spacing: 1px;
              }
              .header .address {
                font-size: 10px;
                margin: 2px 0;
                line-height: 1.3;
              }
              .header .tax-info {
                font-size: 9px;
                margin-top: 4px;
                color: #555;
              }
              .qr-section {
                text-align: center;
                padding: 10px 0;
                border-top: 1px dashed #000;
                border-bottom: 1px dashed #000;
                margin: 10px 0;
              }
              .qr-section img {
                width: 120px;
                height: 120px;
                margin: 5px 0;
              }
              .qr-section p {
                font-size: 10px;
                font-weight: bold;
                margin-top: 4px;
              }
              .bill-info {
                margin: 10px 0;
                border-bottom: 1px dashed #000;
                padding-bottom: 8px;
              }
              .bill-info div {
                display: flex;
                justify-content: space-between;
                margin: 3px 0;
                font-size: 10px;
              }
              .bill-info .label { font-weight: bold; }
              .items-header {
                display: grid;
                grid-template-columns: 2fr 1fr 1fr 1fr;
                gap: 4px;
                border-bottom: 2px solid #000;
                padding: 5px 0;
                font-weight: bold;
                font-size: 10px;
              }
              .item-row {
                display: grid;
                grid-template-columns: 2fr 1fr 1fr 1fr;
                gap: 4px;
                padding: 6px 0;
                border-bottom: 1px dashed #ccc;
                font-size: 10px;
              }
              .item-row .name { font-weight: bold; }
              .item-row .qty, .item-row .price, .item-row .total {
                text-align: right;
              }
              .totals {
                margin-top: 10px;
                border-top: 2px solid #000;
                padding-top: 8px;
              }
              .totals div {
                display: flex;
                justify-content: space-between;
                margin: 4px 0;
                font-size: 11px;
              }
              .totals .grand-total {
                font-size: 14px;
                font-weight: bold;
                border-top: 2px solid #000;
                padding-top: 8px;
              }
              .payment-info {
                text-align: center;
                margin: 10px 0;
                padding: 8px;
                background: #f5f5f5;
                border: 1px solid #ddd;
                border-radius: 4px;
                font-size: 11px;
              }
              .footer {
                text-align: center;
                margin-top: 15px;
                padding-top: 10px;
                border-top: 2px dashed #000;
                font-size: 10px;
              }
              .footer p { margin: 3px 0; }
            </style>
          </head>
          <body>
            <div class="header">
              ${user?.restaurantLogo ? `<img src="${user?.restaurantLogo}" alt="Logo" class="logo">` : ''}
              <h1>${billData.restaurantName}</h1>
              <div class="address">${billData.restaurantAddress}</div>
              ${billData.restaurantPhone ? `<div class="phone">📞 ${billData.restaurantPhone}</div>` : ''}
              <div class="tax-info">
                ${billData.restaurantGST ? `GST: ${billData.restaurantGST}` : ''}
                ${billData.restaurantGST && billData.restaurantFSSAI ? ' | ' : ''}
                ${billData.restaurantFSSAI ? `FSSAI: ${billData.restaurantFSSAI}` : ''}
              </div>
            </div>
            <div class="bill-info">
              <div><span class="label">Bill #:</span><span>${billData.billNumber}</span></div>
              <div><span class="label">Table:</span><span>${billData.tableNumber}</span></div>
              <div><span class="label">Customer:</span><span>${billData.customerName}</span></div>
              ${billData.isMc ? '<div><span class="label" style="color: #dc2626;">MC:</span><span style="color: #dc2626; font-weight: bold;">MANAGEMENT COMPLIMENTERY</span></div>' : ''}
              ${billData.complimentary ? `<div><span class="label" style="color: #059669;">Complimentary:</span><span style="color: #059669;">${billData.complimentary}</span></div>` : ''}
              ${billData.gstNumber ? `<div><span class="label">GST Number:</span><span>${billData.gstNumber}</span></div>` : ''}
              ${billData.customerMobile ? `<div><span class="label">Mobile:</span><span>${billData.customerMobile}</span></div>` : ''}
              <div><span class="label">Date:</span><span>${new Date(billData.orderTime).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}</span></div>
            </div>
            <div class="items-header">
              <span>ITEM</span>
              <span style="text-align: right;">QTY</span>
              <span style="text-align: right;">PRICE</span>
              <span style="text-align: right;">TOTAL</span>
            </div>
            ${billData.items.map(item => `
              <div class="item-row" style="${item.bogoApplied ? 'background: #d4edda; border-left: 3px solid #28a745; padding-left: 8px;' : ''}">
                <span class="name">
                  ${item.name}
                  ${item.bogoApplied && item.bogoDetails ? `<br/><small style="color: #28a745; font-size: 9px;">🎁 ${item.bogoDetails.offerText} (${item.bogoDetails.freeQuantity} free)</small>` : ''}
                </span>
                <span class="qty">${item.quantity}</span>
                <span class="price">₹${item.price}</span>
                <span class="total">₹${item.total}</span>
              </div>
            `).join('')}
            <div class="totals">
              <div><span>Subtotal:</span><span>₹${Number(billData.subtotal).toFixed(2)}</span></div>
              ${Number(billData.discount) > 0 ? `
                <div style="color: #16a34a;">
                  <span>Discount ${billData.discountType === 'percentage' ? '(%)' : ''}:</span>
                  <span>- ₹${Number(billData.discount).toFixed(2)}</span>
                </div>
              ` : ''}
              ${billData.vendorDiscount?.applied ? `
                <div style="color: #16a34a;">
                  <span>Vendor Discount:</span>
                  <span>- ₹${Number(billData.vendorDiscount.discountAmount).toFixed(2)}</span>
                </div>
              ` : ''}
              <div><span>CGST:</span><span>₹${Number(billData.cgstAmount).toFixed(2)}</span></div>
              <div><span>SGST:</span><span>₹${Number(billData.sgstAmount).toFixed(2)}</span></div>
              <div class="grand-total">
                <span>${billData.isMc ? 'GRAND TOTAL (MC):' : 'GRAND TOTAL:'}</span>
                <span>₹${Number(billData.total).toFixed(2)}</span>
              </div>
            </div>
            ${billData.upiQRCode ? `
              <div class="qr-section">
                <img src="${billData.upiQRCode}" alt="UPI QR Code">
                <p>📱 SCAN TO PAY 📱</p>
              </div>
            ` : ''}
            <div class="footer">
              <p>━━━━━━━━━━━━━━━━━━━━</p>
              <p style="font-weight: bold; margin: 8px 0;">THANK YOU!</p>
              <p>Visit Again 😊</p>
            </div>
          </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.print();

      if (activeTab === 'closed' || isTableBillGenerated(selectedTable)) {
        toast.success('Bill printed successfully');
      } else {
        toast.success('Table closed and bill generated');
      }

      setShowCloseModal(false);
      setGstNumber('');
      fetchOpenTables();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to process bill';
      toast.error(errorMessage);
    }
  };

  const calculateTotal = (table) => {
    let total = table.totalAmount || 0;
    if (discount.amount > 0) {
      if (discount.type === 'percentage') {
        total = total * (1 - discount.amount / 100);
      } else {
        total = total - discount.amount;
      }
    }
    return total.toFixed(2);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-500"></div>
      </div>
    );
  }

  const currentTables = activeTab === 'open' ? openTables : closedTables;
  const currentPage = activeTab === 'open' ? openCurrentPage : closedCurrentPage;
  const setCurrentPage = activeTab === 'open' ? setOpenCurrentPage : setClosedCurrentPage;
  const totalTables = activeTab === 'open' ? totalOpenTables : totalClosedTables;

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 p-4 md:p-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-2">
        <h1 className="text-lg font-bold text-gray-900">
          Tables Management
        </h1>
        <p className="text-gray-500 text-xs mt-1">
          Manage dining tables and bills
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-2">
        <div className="flex flex-col sm:flex-row">
          <button
            onClick={() => setActiveTab('open')}
            className={`flex-1 px-4 py-2 font-semibold text-sm transition-all duration-200 ${activeTab === 'open'
                ? 'text-green-600 border-b-2 border-green-500 bg-green-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
          >
            Open Bills ({totalOpenTables})
          </button>
          <button
            onClick={() => setActiveTab('closed')}
            className={`flex-1 px-4 py-2 font-semibold text-sm transition-all duration-200 ${activeTab === 'closed'
                ? 'text-red-600 border-b-2 border-red-500 bg-red-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
          >
            Closed Bills ({totalClosedTables})
          </button>
        </div>
      </div>

      {/* Filter Section - Only show for Closed Bills tab */}
      {activeTab === 'closed' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-4">
  <div className="flex flex-col gap-2">
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-gray-700 font-medium text-xs whitespace-nowrap">
        Filter By:
      </span>

      <div className="flex flex-wrap gap-1.5 items-center">
        {[
          { value: 'all', label: 'All Time', icon: '📅' },
          { value: 'today', label: 'Today', icon: '☀️' },
          { value: 'yesterday', label: 'Yesterday', icon: '🌙' },
          { value: 'thisWeek', label: 'This Week', icon: '📆' },
          { value: 'thisMonth', label: 'This Month', icon: '🗓️' }
        ].map((filter) => (
          <button
            key={filter.value}
            onClick={() => {
              setClosedBillsFilter(filter.value);
              setCustomStartDate('');
              setCustomEndDate('');
            }}
            className={`px-2 py-1 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${
              closedBillsFilter === filter.value
                ? 'bg-red-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <span>{filter.icon}</span>
            <span>{filter.label}</span>
          </button>
        ))}

        <button
          onClick={() => setClosedBillsFilter('custom')}
          className={`px-2 py-1 rounded-full text-xs font-medium transition-all flex items-center gap-1 border ${
            closedBillsFilter === 'custom'
              ? 'bg-red-500 text-white border-red-500'
              : 'border-red-300 text-red-600 hover:bg-red-50'
          }`}
        >
          <span>🎯</span>
          <span>Custom</span>
        </button>

        {closedBillsFilter !== 'all' && (
          <button
            onClick={() => {
              setClosedBillsFilter('all');
              setCustomStartDate('');
              setCustomEndDate('');
            }}
            className="px-2 py-1 bg-gray-600 text-white rounded-full text-xs font-medium hover:bg-gray-700 flex items-center gap-1"
          >
            <span>✨</span>
            <span>Clear</span>
          </button>
        )}
      </div>
    </div>

    {closedBillsFilter === 'custom' && (
      <div className="mt-2 pt-2 border-t border-gray-200">
        <div className="flex flex-col gap-2">
          <span className="text-gray-700 font-medium text-xs">
            Date Range:
          </span>

          <div className="flex flex-col sm:flex-row gap-2 items-center">
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:border-red-500 focus:outline-none"
            />

            <span className="text-gray-500 text-xs">to</span>

            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:border-red-500 focus:outline-none"
            />
          </div>
        </div>
      </div>
    )}
  </div>
</div>
      )}

          {/* Tables Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 mb-6">
            {currentTables.map((table) => {
              const isPending = hasPendingOrders(table);
              return (
                <div key={table._id} className={`bg-white rounded-xl shadow-sm border-2 p-4 sm:p-6 transition-all duration-200 hover:shadow-lg ${isPending ? 'border-orange-500 shadow-orange-100' : 'border-green-500 shadow-green-100'
                  }`}>
                  {/* Header */}
                  <div className="flex justify-between items-center mb-3 sm:mb-4 pb-2 sm:pb-3 border-b border-gray-200">
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                      Table {table.tableId?.tableNumber}
                    </h2>
                    <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold ${activeTab === 'open'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                      }`}>
                      {activeTab === 'open' ? 'Active' : 'Closed'}
                    </span>
                  </div>

                  {/* Info Grid */}
                  <div className="space-y-2 sm:space-y-3 mb-3 sm:mb-4">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <FiUser className="text-gray-500 text-sm sm:text-lg flex-shrink-0" />
                      <div className="text-gray-700 min-w-0 flex-1">
                        <div className="text-sm sm:text-base"><strong>Customer:</strong> {table.customerId?.fullName || 'Guest'}</div>
                        {table.isMc && (
                          <div className="mt-1">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              MC
                            </span>
                          </div>
                        )}
                        {table.complimentary && (
                          <div className="mt-1 text-xs text-gray-600 max-w-[150px] sm:max-w-[200px] truncate" title={table.complimentary}>
                            🎁 {table.complimentary}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 sm:gap-3">
                      <FiClock className="text-gray-500 text-sm sm:text-lg flex-shrink-0" />
                      <span className="text-gray-700 text-sm sm:text-base">
                        <strong>Duration:</strong> {getDuration(table.startTime)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 sm:gap-3">
                      <FiShoppingBag className="text-gray-500 text-sm sm:text-lg flex-shrink-0" />
                      <span className="text-gray-700 text-sm sm:text-base">
                        <strong>Orders:</strong> {table.orders?.length || 0}
                      </span>
                    </div>
                  </div>

                  {/* Total Amount */}
                  <div className="bg-gradient-to-r from-green-500 to-green-600 p-3 rounded-lg mb-2 flex items-center justify-center gap-2">
                    <p className="text-white text-sm font-medium">
                      Total Amount:
                    </p>
                    <p className="text-white text-base font-bold">
                      ₹{table.totalAmount?.toFixed(2) || '0.00'}
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-2 sm:space-y-3">
                    {
                      !isTableBillGenerated(table) &&
                      <button
                        className="w-full px-2 sm:px-3 py-1.5 sm:py-2 bg-blue-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-1.5 shadow-md hover:shadow-lg text-xs sm:text-sm"
                        onClick={() => handleOpenAddOrder(table)}
                        title="Add Order"
                      >
                        <FiPlus className="text-xs sm:text-sm" />
                        <span className="font-medium text-xs sm:text-sm">Add Order</span>
                      </button>
                    }

                    <div className={`grid gap-1.5 sm:gap-2 ${isTableBillGenerated(table) ? 'grid-cols-3' : 'grid-cols-2'}`}>
                      <button
                        className="px-1.5 sm:px-2 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-1 shadow-md hover:shadow-lg text-xs"
                        onClick={() => openOrdersModal(table)}
                      >
                        <FiEye className="text-xs" />
                        <span className="hidden xs:inline sm:inline text-xs">View Orders</span>
                        <span className="xs:hidden sm:hidden">View</span>
                      </button>
                      <button
                        className={`px-1.5 sm:px-2 py-1.5 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-1 shadow-md hover:shadow-lg text-xs ${isTableBillGenerated(table)
                            ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                            : 'bg-red-500 hover:bg-red-600 text-white'
                          }
                    `}
                        onClick={() => openCloseModal(table)}
                        disabled={isTableBillGenerated(table)}
                        title={isTableBillGenerated(table) ? "Bill Already Generated" : "Generate Bill"}
                      >
                        {isTableBillGenerated(table) ? (
                          <>
                            <span className="text-green-600 text-xs">✓</span>
                            <span className="hidden xs:inline sm:inline text-xs">Bill Done</span>
                            <span className="xs:hidden sm:hidden">Done</span>
                          </>
                        ) : (
                          <>
                            <FiX className="text-xs" />
                            <span className="hidden xs:inline sm:inline text-xs">Generate Bill</span>
                            <span className="xs:hidden sm:hidden">Bill</span>
                          </>
                        )}
                      </button>
                      {isTableBillGenerated(table) && (
                        <button
                          className="px-1.5 sm:px-2 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-1 shadow-md hover:shadow-lg text-xs"
                          onClick={() => openBillUpdateModal(table)}
                          title="Update Bill"
                        >
                          <FiFileText className="text-xs" />
                          <span className="hidden xs:inline sm:inline text-xs">Update Bill</span>
                          <span className="xs:hidden sm:hidden">Update</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {currentTables.length === 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiFileText className="text-gray-400 text-3xl" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {activeTab === 'open' ? 'No open tables at the moment' : 'No closed tables found'}
              </h3>
              <p className="text-gray-600">
                {activeTab === 'open'
                  ? 'New table orders will appear here when customers place orders.'
                  : 'Closed tables will appear here after bills are generated.'
                }
              </p>
            </div>
          )}

          {/* Pagination */}
          {Math.ceil(totalTables / itemsPerPage) > 1 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-4">
              <div className="flex flex-col gap-3 sm:gap-4">
                <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                  Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalTables)}-{Math.min(currentPage * itemsPerPage, totalTables)} of {totalTables} tables
                </div>
                <div className="flex flex-col sm:flex-row justify-center sm:justify-between items-center gap-3">
                  <div className="flex items-center gap-2 justify-center">
                    <button
                      className="px-2 sm:px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      <span className="hidden sm:inline">Previous</span>
                      <span className="sm:hidden">‹</span>
                    </button>
                    <div className="flex items-center gap-1 sm:gap-2">
                      {[...Array(Math.ceil(totalTables / itemsPerPage))].map((_, idx) => {
                        const pageNum = idx + 1;
                        const totalTablePages = Math.ceil(totalTables / itemsPerPage);
                        if (
                          pageNum === 1 ||
                          pageNum === totalTablePages ||
                          (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={pageNum}
                              className={`w-7 h-7 sm:w-9 sm:h-9 rounded-lg font-medium transition-colors duration-200 text-xs sm:text-sm ${pageNum === currentPage
                                  ? 'bg-red-500 text-white'
                                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                }`}
                              onClick={() => setCurrentPage(pageNum)}
                            >
                              {pageNum}
                            </button>
                          );
                        } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                          return <span key={pageNum} className="px-1 sm:px-2 text-gray-400 text-xs sm:text-sm">...</span>;
                        }
                        return null;
                      })}
                      <button
                        className="px-2 sm:px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                        onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalTables / itemsPerPage), prev + 1))}
                        disabled={currentPage === Math.ceil(totalTables / itemsPerPage)}
                      >
                        <span className="hidden sm:inline">Next</span>
                        <span className="sm:hidden">›</span>
                      </button>
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm justify-center">
                      <span className="text-gray-600">Show</span>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="px-2 py-1 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none text-xs sm:text-sm"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                      <span className="text-gray-600">per page</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* View Orders Modal */}
          {showOrdersModal && selectedTable && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
              <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto m-2 sm:m-0" onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-6">
                  <div className="flex justify-between items-center gap-4">
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">Table {selectedTable.tableId?.tableNumber} - Orders</h2>
                    <button
                      className="text-gray-400 hover:text-gray-600 text-xl sm:text-2xl font-bold flex-shrink-0"
                      onClick={() => setShowOrdersModal(false)}
                    >
                      ×
                    </button>
                  </div>
                </div>
                <div className="p-4 sm:p-6">
                  {/* Table Info */}
                  <div className="bg-blue-50 p-3 sm:p-4 rounded-lg mb-4 sm:mb-6">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                      <div>
                        <p className="text-xs sm:text-sm text-gray-600 mb-1">Customer</p>
                        <p className="font-bold text-sm sm:text-base text-gray-900 truncate">{selectedTable.customerId?.fullName || 'Guest'}</p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-gray-600 mb-1">Duration</p>
                        <p className="font-bold text-sm sm:text-base text-gray-900">{getDuration(selectedTable.startTime)}</p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-gray-600 mb-1">Total Amount</p>
                        <p className="font-bold text-lg sm:text-xl text-green-600">₹{selectedTable.isMc ? '0.00' : (selectedTable.totalAmount?.toFixed(2) || '0.00')}</p>
                      </div>
                    </div>
                  </div>

                  {/* Orders List */}
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-blue-600 mb-3 sm:mb-4">Orders ({selectedTable.orders?.length || 0})</h3>
                    {selectedTable.orders && selectedTable.orders.length > 0 ? (
                      <div className="space-y-4">
                        {selectedTable.orders.map((orderRef, index) => {
                          const order = orderRef.orderId;
                          if (!order) return null;

                          return (
                            <div key={index} className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-white">
                              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3">
                                <div>
                                  <strong className="text-base sm:text-lg">{order.orderNumber}</strong>
                                  <span className="ml-0 sm:ml-3 text-xs sm:text-sm text-gray-600 block sm:inline">
                                    {new Date(order.createdAt).toLocaleTimeString()}
                                  </span>
                                </div>
                                <span
                                  className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium cursor-pointer transition-all duration-200 self-start sm:self-auto ${getStatusBadge(order.status)
                                    } ${order.status === 'completed' || order.status === 'cancelled'
                                      ? 'cursor-not-allowed'
                                      : 'hover:opacity-80'
                                    }`}
                                  onClick={() => handleStatusClick(order._id, order.status)}
                                  title={order.status === 'completed' || order.status === 'cancelled' ? 'Cannot change status' : `Click to move to ${getNextStatus(order.status)}`}
                                >
                                  {order.status}
                                </span>
                              </div>

                              {/* Order Items */}
                              <div className="mb-3">
                                <p className="text-xs sm:text-sm text-gray-600 mb-2"><strong>Items:</strong></p>
                                <div className="space-y-2">
                                  {order.items?.map((item, idx) => (
                                    <div
                                      key={idx}
                                      className={`p-2 sm:p-3 rounded-lg ${item.bogoApplied ? 'bg-green-50 border border-green-200' : 'bg-gray-50'
                                        }`}
                                    >
                                      <div className="flex justify-between items-start gap-2">
                                        <span className="text-sm sm:text-base flex-1">
                                          {item.menuItemId?.name || item.name || 'Item'} × {item.quantity}
                                          {item.bogoApplied && (
                                            <span className="ml-2 px-2 py-1 bg-green-600 text-white text-xs rounded-full font-bold inline-block">
                                              BOGO
                                            </span>
                                          )}
                                        </span>
                                        <span className="font-medium text-sm sm:text-base flex-shrink-0">₹{(item.price * item.quantity).toFixed(2)}</span>
                                      </div>
                                      {item.bogoApplied && item.bogoDetails && (
                                        <div className="text-xs text-green-700 font-bold mt-2">
                                          🎁 {item.bogoDetails.offerText} ({item.bogoDetails.freeQuantity} free)
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Pricing Breakdown */}
                              <div className="pt-3 border-t border-gray-200">
                                <div className="flex justify-between text-sm mb-1">
                                  <span className="text-gray-600">Subtotal:</span>
                                  <span>₹{(Number(order.pricing?.subtotal) || 0).toFixed(2)}</span>
                                </div>
                                {order.pricing?.discount > 0 && (
                                  <div className="flex justify-between text-sm mb-1 text-green-600">
                                    <span>Discount:</span>
                                    <span>- ₹{(Number(order.pricing?.discount) || 0).toFixed(2)}</span>
                                  </div>
                                )}
                                {order.pricing?.vendorDiscount?.applied && (
                                  <div className="flex justify-between text-sm mb-1 text-green-600">
                                    <span>Vendor Discount:</span>
                                    <span>- ₹{(Number(order.pricing?.vendorDiscount?.discountAmount) || 0).toFixed(2)}</span>
                                  </div>
                                )}
                                <div className="flex justify-between text-sm mb-1">
                                  <span className="text-gray-600">CGST:</span>
                                  <span>₹{((Number(order.pricing?.gstAmount) || 0) / 2).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-sm mb-2">
                                  <span className="text-gray-600">SGST:</span>
                                  <span>₹{((Number(order.pricing?.gstAmount) || 0) / 2).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between pt-2 border-t border-gray-200">
                                  <span className="font-bold">Order Total:</span>
                                  <span className="font-bold text-lg text-green-600">₹{selectedTable.isMc ? '0.00' : (order.pricing?.total?.toFixed(2) || '0.00')}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                          <FiShoppingBag className="text-gray-400 text-2xl" />
                        </div>
                        <p className="text-gray-600">No orders placed yet</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <button
                      className="px-4 sm:px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 text-sm sm:text-base"
                      onClick={() => setShowOrdersModal(false)}
                    >
                      Close
                    </button>
                    <button
                      className="px-4 sm:px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2 text-sm sm:text-base"
                      onClick={() => {
                        setShowOrdersModal(false);
                        openCloseModal(selectedTable);
                      }}
                    >
                      <FiX className="text-sm sm:text-base" />
                      <span>{isTableBillGenerated(selectedTable) ? 'Print Bill' : 'Generate Bill'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Close Table Modal */}
          {showCloseModal && selectedTable && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
              <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto m-2 sm:m-0" onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-6">
                  <div className="flex justify-between items-center gap-4">
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">Close Table {selectedTable.tableId?.tableNumber}</h2>
                    <button
                      className="text-gray-400 hover:text-gray-600 text-xl sm:text-2xl font-bold flex-shrink-0"
                      onClick={() => setShowCloseModal(false)}
                    >
                      ×
                    </button>
                  </div>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="bg-gray-50 p-3 sm:p-4 rounded-lg mb-4 sm:mb-6">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                      <div>
                        <p className="text-xs sm:text-sm text-gray-600 mb-1">Customer</p>
                        <p className="font-bold text-sm sm:text-base text-gray-900 truncate">{selectedTable.customerId?.fullName || 'Guest'}</p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-gray-600 mb-1">Orders</p>
                        <p className="font-bold text-sm sm:text-base text-gray-900">{selectedTable.orders?.length || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-gray-600 mb-1">Duration</p>
                        <p className="font-bold text-sm sm:text-base text-gray-900">{Math.round((new Date() - new Date(selectedTable.startTime)) / 60000)} minutes</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 sm:mb-6">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Discount Type</label>
                      <select
                        className="w-full px-3 py-2 sm:px-4 text-sm sm:text-base border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none"
                        value={discount.type}
                        disabled={isTableBillGenerated(selectedTable) || isMc}
                        onChange={(e) => {
                          setDiscount({ ...discount, type: e.target.value });

                          let total = Number(selectedTable.totalAmount || 0);
                          if (Number(discount.amount) > 0) {
                            if (e.target.value === 'percentage') {
                              total = total * (1 - Number(discount.amount) / 100);
                            } else {
                              total = total - Number(discount.amount);
                            }
                          }
                          setCashOnly(false);
                          setPayment((prev) => ({ ...prev, onlineAmount: total.toFixed(2), cashAmount: 0 }))
                        }}
                      >
                        <option value="percentage">Percentage (%)</option>
                        <option value="flat">Flat Amount (₹)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                        Discount {discount.type === 'percentage' ? 'Percentage' : 'Amount'}
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 sm:px-4 text-sm sm:text-base border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none"
                        disabled={isTableBillGenerated(selectedTable) || isMc}
                        value={discount.amount}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          setDiscount({ ...discount, amount: value });
                        }}
                        onBlur={() => {
                          let total = Number(selectedTable.totalAmount || 0);
                          if (Number(discount.amount) > 0) {
                            if (discount.type === 'percentage') {
                              total = total * (1 - Number(discount.amount) / 100);
                            } else {
                              total = total - Number(discount.amount);
                            }
                          }
                          setCashOnly(false);
                          setPayment((prev) => ({ ...prev, onlineAmount: total.toFixed(2), cashAmount: 0 }))
                        }}
                        placeholder={discount.type === 'percentage' ? 'Enter percentage' : 'Enter amount'}
                      />
                    </div>
                  </div>



                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 sm:mb-6">
                    <div>
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-start gap-2 sm:gap-7 text-xs sm:text-sm font-medium text-gray-700 mb-2">
                        <span>Cash Amount (₹)</span>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            className={`w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${isTableBillGenerated(selectedTable) || isMc ? 'bg-gray-100 cursor-not-allowed' : ''
                              }`}
                            checked={cashOnly}
                            onChange={(e) => {
                              if (isTableBillGenerated(selectedTable) || isMc) return;
                              setCashOnly(e.target.checked);

                              if (e.target.checked) {
                                let cashAmount = Number(payment.cashAmount) + Number(payment.onlineAmount);
                                setPayment({ cashAmount: cashAmount, onlineAmount: 0 });
                              } else {
                                let onlineAmount = Number(payment.cashAmount) + Number(payment.onlineAmount);
                                setPayment({ cashAmount: 0, onlineAmount: onlineAmount });
                              }
                            }}
                            disabled={isTableBillGenerated(selectedTable) || isMc}
                          />
                          <span>Cash Only</span>
                        </div>
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 sm:px-4 text-sm sm:text-base border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none"
                        value={payment.cashAmount}
                        inputMode="decimal"
                        disabled={isTableBillGenerated(selectedTable) || isMc || cashOnly}
                        placeholder="Enter cash amount"
                        onChange={(e) => {
                          if (isTableBillGenerated(selectedTable) || isMc || cashOnly) return;
                          let value = e.target.value.replace(/[^0-9.]/g, '');
                          const parts = value.split('.');
                          if (parts.length > 2) {
                            value = parts[0] + '.' + parts.slice(1).join('');
                          }

                          const billTotal = Number(calculateTotal(selectedTable).replace(/,/g, ''));
                          const cashAmt = Number(value) || 0;
                          const remainingAmt = billTotal - cashAmt;
                          const onlineAmt = remainingAmt > 0 ? remainingAmt.toFixed(2) : '';

                          setPayment({ cashAmount: value, onlineAmount: onlineAmt });
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Online Amount (₹)</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 sm:px-4 text-sm sm:text-base border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none"
                        value={payment.onlineAmount}
                        inputMode="decimal"
                        disabled={isTableBillGenerated(selectedTable) || isMc || cashOnly}
                        placeholder="Auto-filled or enter manually"
                        onChange={(e) => {
                          if (isTableBillGenerated(selectedTable) || isMc || cashOnly) return;
                          let value = e.target.value.replace(/[^0-9.]/g, '');
                          const parts = value.split('.');
                          if (parts.length > 2) {
                            value = parts[0] + '.' + parts.slice(1).join('');
                          }

                          const billTotal = Number(calculateTotal(selectedTable).replace(/,/g, ''));
                          const onlineAmt = Number(value) || 0;
                          const remainingAmt = billTotal - onlineAmt;
                          const cashAmt = remainingAmt > 0 ? remainingAmt.toFixed(2) : '';

                          setPayment({ cashAmount: cashAmt, onlineAmount: value });
                        }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cash Received from Customer (₹)
                      </label>
                      <input
                        type="text"
                        className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none ${isTableBillGenerated(selectedTable)
                            ? 'bg-gray-100 text-gray-600 cursor-not-allowed'
                            : 'border-gray-300'
                          }`}
                        value={cashReceived}
                        inputMode="decimal"
                        placeholder="Enter cash received amount"
                        onChange={(e) => {
                          if (isTableBillGenerated(selectedTable)) return;
                          let value = e.target.value.replace(/[^0-9.]/g, '');
                          const parts = value.split('.');
                          if (parts.length > 2) {
                            value = parts[0] + '.' + parts.slice(1).join('');
                          }
                          setCashReceived(value);
                        }}
                        disabled={isTableBillGenerated(selectedTable)}
                      />
                    </div>

                    <div>
                      <div className="mb-2 h-[20px]"></div>

                      {cashReceived &&
                        Number(cashReceived) > 0 &&
                        Number(payment.cashAmount) > 0 && (
                          <div className="p-2 bg-green-50 rounded border border-green-200">
                            <p className="text-sm font-medium text-green-800">
                              Refund/Change: ₹
                              {(Number(cashReceived) - Number(payment.cashAmount)).toFixed(2)}
                            </p>
                          </div>
                        )}
                    </div>
                  </div>


                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">GST Number</label>
                      <input
                        type="text"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none"
                        disabled={isTableBillGenerated(selectedTable) || isMc}
                        value={gstNumber}
                        placeholder='Enter GST Number'
                        onChange={(e) => {
                          setGstNumber(e.target.value.toUpperCase());
                        }}
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
                          disabled={isTableBillGenerated(selectedTable)}
                          checked={isMc}
                          onChange={(e) => {
                            const isMcChecked = e.target.checked;
                            setIsMc(isMcChecked);

                            if (isMcChecked) {
                              // When MC is checked, set all amounts to 0 and default complimentary
                              setDiscount({ amount: 0, type: 'percentage' });
                              setPayment({ cashAmount: 0, onlineAmount: 0 });
                              setComplimentary('');
                              setGstNumber('');
                            } else {
                              // When MC is unchecked, reset to original values
                              setDiscount({ amount: 0, type: 'percentage' });
                              setPayment({ cashAmount: '', onlineAmount: selectedTable.totalAmount || 0 });
                              setComplimentary('');
                            }
                          }}
                        />
                        <span className="text-sm font-medium text-gray-700">Is Complimentary</span>
                      </label>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Complimentary</label>
                    <textarea
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none resize-none"
                      rows="3"
                      value={complimentary}
                      placeholder="Enter complimentary (if any)"
                      onChange={(e) => {
                        if (isTableBillGenerated(selectedTable)) return;
                        setComplimentary(e.target.value);
                      }}
                      disabled={isTableBillGenerated(selectedTable)}
                    />
                  </div>

                  {(Number(payment.cashAmount) > 0 || Number(payment.onlineAmount) > 0) && (
                    <div className="bg-gray-100 p-4 rounded-lg mb-6">
                      <p className="font-semibold mb-2">Payment Breakdown:</p>
                      {Number(payment.cashAmount) > 0 && (
                        <p className="text-green-600 mb-1">💵 Cash: ₹{Number(payment.cashAmount).toFixed(2)}</p>
                      )}
                      {Number(payment.onlineAmount) > 0 && (
                        <p className="text-blue-600 mb-2">💳 Online: ₹{Number(payment.onlineAmount).toFixed(2)}</p>
                      )}
                      <p className="font-bold">
                        Total Paid: ₹{(Number(payment.cashAmount || 0) + Number(payment.onlineAmount || 0)).toFixed(2)}
                      </p>
                    </div>
                  )}

                  <div className="bg-green-50 p-4 rounded-lg">
                    <p><strong>Original Amount:</strong> ₹{selectedTable.totalAmount?.toFixed(2) || 0}</p>
                    {Number(discount.amount) > 0 && !isMc && (
                      <p className="text-green-600">
                        <strong>Discount:</strong> - ₹{
                          discount.type === 'percentage'
                            ? (((selectedTable.totalAmount || 0) * Number(discount.amount)) / 100).toFixed(2)
                            : Number(discount.amount).toFixed(2)
                        }
                      </p>
                    )}
                    <p className="text-xl font-bold mt-2">
                      <strong>Final Amount:</strong> ₹{isMc ? '0.00' : calculateTotal(selectedTable)}
                    </p>
                  </div>
                </div>
                <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <button
                      className="px-4 sm:px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 text-sm sm:text-base"
                      onClick={() => setShowCloseModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-4 sm:px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2 text-sm sm:text-base"
                      onClick={generateBill}
                    >
                      <FiX className="text-sm sm:text-base" />
                      <span>{isTableBillGenerated(selectedTable) ? 'Print Bill' : 'Close Table & Generate Bill'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Add Order Modal */}
          <AddOrderModal
            showCreateOrderModal={showCreateOrderModal}
            setShowCreateOrderModal={setShowCreateOrderModal}
            customerData={customerData}
          />

          {/* Table Bill Update Modal */}
          <TableBillUpdateModal
            show={showTableBillUpdateModal}
            table={selectedTableForUpdate}
            onHide={() => {
              setShowTableBillUpdateModal(false);
              setSelectedTableForUpdate(null);
            }}
            onSuccess={() => {
              setShowTableBillUpdateModal(false);
              setSelectedTableForUpdate(null);
              fetchOpenTables(); // Refresh tables
              toast.success('Table bill update request submitted successfully');
            }}
          />
        </div>
      );
};

export default OpenTables;
